import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getExportStatus, downloadExport } from "../../services/export.service";
import "../../styles/studio/exportPage.css";

const POLL_INTERVAL_MS = 5000;

const PHASE_LABELS = {
  queued: "Queued — waiting to start...",
  retrying: "Retrying...",
  rendering: "Rendering frames...",
  audio_download: "Downloading audio...",
  audio_merge: "Merging audio...",
  processing: "Processing...",
  done: "Complete",
  error: "Failed",
  cancelled: "Cancelled",
  downloaded: "Already downloaded",
};

const ExportPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef(null);

  const isActive = (s) => s === "queued" || s === "processing" || s === "retrying";

  const fetchStatus = async () => {
    try {
      const data = await getExportStatus(jobId);
      setStatus(data);
      setError("");

      if (!isActive(data.status)) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (err) {
      setError(err.message || "Failed to load export status");
      clearInterval(pollRef.current);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => clearInterval(pollRef.current);
  }, [jobId]);

  useEffect(() => {
    if (!status) return;
    if (isActive(status.status) && !pollRef.current) {
      pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    }
  }, [status?.status]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadExport(jobId, "export", {
        exportType: status.exportType,
        quality: status.quality,
        layout: status.layout,
        startTime: status.startTime,
        endTime: status.endTime,
        time: status.time,
      });
      await fetchStatus();
    } catch (err) {
      setError(err.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="export-page">
        <div className="export-card">
          <p className="export-loading">Loading export status...</p>
        </div>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div className="export-page">
        <div className="export-card">
          <p className="export-error">{error}</p>
          <button className="export-btn" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const phaseLabel = PHASE_LABELS[status?.phase] || PHASE_LABELS[status?.status] || "Processing...";
  const isDone = status?.status === "done";
  const isFailed = status?.status === "error";
  const isDownloaded = status?.status === "downloaded";
  const isCancelled = status?.status === "cancelled";
  const isProcessing = isActive(status?.status);

  return (
    <div className="export-page">
      <div className="export-card">
        <h1 className="export-title">Export</h1>

        <div className="export-meta">
          <span className="export-type">{status?.exportType === "image" ? "Image" : "Video"}</span>
          <span className="export-quality">{status?.quality}</span>
          <span className="export-layout">{status?.layout}</span>
        </div>

        {/* Progress bar */}
        {isProcessing && (
          <div className="export-progress-wrap">
            <div className="export-progress-bar">
              <div
                className="export-progress-fill"
                style={{ width: `${status?.progress || 0}%` }}
              />
            </div>
            <span className="export-progress-label">{status?.progress || 0}%</span>
          </div>
        )}

        {/* Phase */}
        <p className={`export-phase ${isFailed ? "error" : isDone ? "done" : ""}`}>
          {phaseLabel}
        </p>

        {/* ETA */}
        {isProcessing && status?.etaSeconds != null && (
          <p className="export-eta">
            Est. {Math.ceil(status.etaSeconds / 60)} min remaining
          </p>
        )}

        {/* Error message */}
        {isFailed && status?.error && (
          <p className="export-error-msg">{status.error}</p>
        )}

        {error && <p className="export-error">{error}</p>}

        {/* Actions */}
        <div className="export-actions">
          {isDone && (
            <button
              className="export-btn primary"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? "Downloading..." : "Download"}
            </button>
          )}

          {isDownloaded && (
            <p className="export-downloaded-note">
              This export was already downloaded. Files are removed after download.
            </p>
          )}

          {isCancelled && (
            <p className="export-note">This export was cancelled.</p>
          )}

          {isFailed && (
            <p className="export-note">Please try exporting again from the animation editor.</p>
          )}

          <button className="export-btn" onClick={() => navigate("/studio/animations")}>
            My Animations
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportPage;
